/**
 * Camera Cleanup Utility
 * Ensures proper cleanup of camera resources before new camera access
 */

import { cameraLogger } from './logger';

/**
 * Wait for all active media streams to be fully released
 */
export async function ensureCameraCleanup(): Promise<void> {
  return new Promise((resolve) => {
    // Give time for any active streams to be properly released
    setTimeout(() => {
      // Force garbage collection if available (development only)
      if (
        typeof window !== 'undefined' &&
        'gc' in window &&
        typeof (window as { gc?: () => void }).gc === 'function'
      ) {
        (window as { gc: () => void }).gc();
      }
      resolve();
    }, 200);
  });
}

/**
 * Stop all tracks on a media stream and ensure cleanup
 */
export function stopStreamTracks(stream: MediaStream | null): void {
  if (stream) {
    cameraLogger.debug(`Stopping stream tracks: ${stream.getTracks().length}`);
    stream.getTracks().forEach((track, index) => {
      cameraLogger.debug(
        `Stopping track ${index}: ${track.kind} ${track.readyState}`
      );
      track.stop();
    });
  }
}

/**
 * Check if camera is available and not in use
 */
export async function isCameraAvailable(): Promise<boolean> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter(
      (device) => device.kind === 'videoinput'
    );
    cameraLogger.debug(`Available video devices: ${videoDevices.length}`);
    return videoDevices.length > 0;
  } catch (error) {
    cameraLogger.error('Error checking camera availability:', { error: error instanceof Error ? error : new Error(`${error}`) });
    return false;
  }
}

/**
 * Test camera access without keeping the stream
 */
export async function testCameraAccess(
  constraints: MediaStreamConstraints
): Promise<string | null> {
  try {
    cameraLogger.debug('Testing camera access with constraints:', { labels: { constraints: JSON.stringify(constraints) }});
    const testStream = await navigator.mediaDevices.getUserMedia(constraints);

    // Immediately stop the test stream
    stopStreamTracks(testStream);

    cameraLogger.debug('Camera access test successful');
    return null; // No error
  } catch (error) {
    cameraLogger.error('Camera access test failed:', { error: error instanceof Error ? error : new Error(`${error}`) });

    if (error instanceof Error) {
      if (error.name === 'NotAllowedError') {
        return 'Camera permission denied. Please allow camera access and try again.';
      } else if (error.name === 'NotFoundError') {
        return 'No camera found. Please ensure a camera is connected.';
      } else if (error.name === 'NotReadableError') {
        return 'Camera is already in use by another application.';
      } else if (error.name === 'OverconstrainedError') {
        return 'Camera constraints not supported. Trying with basic settings...';
      } else {
        return error.message;
      }
    }

    return 'Camera access failed for unknown reason';
  }
}
